// The card's aggregator: the period's scanned games that reached a
// middlegame, by their middlegame result (`GameFacts.middlegameResult`:
// Ahead, Comeback, Level, Blown advantage, Behind — from how the middlegame
// started and how it ended), with each result's start × end cells for the
// tooltips. Also the period's games, for the card's scope note. Registered
// with the game store by this plugin's setup; the store runs it over the last
// 30 days' games on every change and keeps the result.
import { aggregatorId, MIDDLEGAME_RESULTS, STAGE_STANDINGS, type Accumulator, type Aggregator, type MiddlegameResult, type StageStanding } from '@chess/api-model';

/** One start × end cell of a result, with its games. */
export interface MiddlegameCell {
  start: StageStanding;
  end: StageStanding;
  count: number;
}

/** What the card shows; plain data, so the card and its stories need no store. */
export interface MiddlegameResultsResult {
  /** Every game of the period, for "N of M games reached a middlegame". */
  games: number;
  /** Games in each result; they add up to the middlegames counted. */
  byResult: Record<MiddlegameResult, number>;
  /** Each result's cells with games, in grid order (start winning → lost, end winning → lost). */
  cells: Record<MiddlegameResult, MiddlegameCell[]>;
}

const empty = <T,>(make: () => T) => ({ ahead: make(), comeback: make(), level: make(), blown: make(), behind: make() }) satisfies Record<MiddlegameResult, T>;

export class MiddlegameResultsAggregator implements Aggregator<MiddlegameResultsResult> {
  readonly id = aggregatorId('middlegame-results');

  start(): Accumulator<MiddlegameResultsResult> {
    const byResult = empty(() => 0);
    const grid = new Map<string, { result: MiddlegameResult; count: number }>();
    let games = 0;
    return {
      add: (game) => {
        games += 1;
        if (!game.scanned || !game.middlegameResult || !game.middlegameStart || !game.middlegameEnd) return;
        byResult[game.middlegameResult] += 1;
        const key = `${game.middlegameStart}:${game.middlegameEnd}`;
        const cell = grid.get(key) ?? { result: game.middlegameResult, count: 0 };
        cell.count += 1;
        grid.set(key, cell);
      },
      result: () => {
        const cells = empty<MiddlegameCell[]>(() => []);
        for (const start of STAGE_STANDINGS)
          for (const end of STAGE_STANDINGS) {
            const cell = grid.get(`${start}:${end}`);
            if (cell) cells[cell.result].push({ start, end, count: cell.count });
          }
        return { games, byResult, cells };
      },
    };
  }
}

/** The games that reached a middlegame (with a start read): every result together. */
export const middlegamesOf = (r: MiddlegameResultsResult) => MIDDLEGAME_RESULTS.reduce((sum, k) => sum + r.byResult[k], 0);
